/**
 * Focus Shell Types
 *
 * Sistema de shell para gestionar múltiples apps FX en tabs.
 * Maneja autenticación, entitlements, tabs y popouts.
 */

/**
 * URLs del backend resueltas desde Firestore (`servidores_origen/{id}`).
 *
 * Permiten que múltiples tenants compartan el mismo bundle de cada app
 * apuntando a backends distintos sin rebuild. focus-backend resuelve
 * `organizations/{orgId}.params.servidor_origen_id` y embebe estas URLs
 * en la respuesta de /entitlements; el shell las propaga a las apps via
 * `orgParams.servidor_origen` y cada app aplica su propia cascada
 * (`servidor_origen → import.meta.env → fallback`).
 */
export interface ServidorOrigenURLs {
  id?: string
  name?: string
  cnf_api_url?: string
  cnf_ws_url?: string
  monitor_api_url?: string
  monitor_ws_url?: string
}

/**
 * Org params expuestos al cliente (subset seguro, sin credenciales).
 *
 * Es el shape que viaja desde focus-backend → Focus Shell → cada app
 * remota via `orgParams` prop. Cualquier campo nuevo que el backend
 * exponga debe agregarse aquí para que TypeScript lo deje pasar
 * sin tener que castear en el shell.
 */
export interface ClientOrgParams {
  price_symbols?: string[]
  trade_accounts?: string[]
  blob_sftp?: string[]
  servidor_origen?: ServidorOrigenURLs
}

/**
 * Usuario autenticado en Focus Shell
 * Integración con Firebase Auth y Firestore
 */
export interface FocusUser {
  uid: string
  email: string
  organizationId: string
  organizationName: string
  role: 'admin' | 'trader' | 'viewer'
  displayName: string
}

/**
 * Tab individual dentro del shell
 * Cada tab representa una instancia de una aplicación
 */
export interface AppTab {
  tabId: string           // UUID único del tab
  appId: string           // ID de la app: 'monitor-contribuciones', 'fxd', etc.
  instanceId: string      // UUID de esta instancia específica de la app
  title: string           // Título mostrado en el tab (puede cambiar dinámicamente)
  isActive: boolean       // Si este tab está actualmente activo
  isPinned?: boolean      // Si el tab está pinneado (no se puede cerrar accidentalmente)
  isPopout?: boolean      // Si el tab está en ventana separada
}

/**
 * Metadata de una aplicación disponible
 * Define las apps que el usuario puede abrir según sus entitlements
 */
export interface AppMetadata {
  appId: string           // ID único: 'monitor-contribuciones', 'fxd', 'btrader'
  code: string            // Código corto de 3 letras: 'MON', 'FXD', 'FIX'
  name: string            // Nombre completo: 'Monitor de Contribuciones'
  description: string     // Descripción breve
  icon: string            // Emoji o nombre de icono Lucide: '📊', 'LineChart'
  version: string         // Versión semántica: '1.0.0'
  config: Record<string, unknown> // Configuración específica de la app
  category?: 'pricing' | 'trading' | 'analytics' | 'admin' // Categoría para menú
  requiresAdmin?: boolean // Si requiere rol admin
}

/**
 * Estado global del Focus Shell
 * Manejado por Zustand store
 */
export interface FocusShellState {
  // Authentication state
  isAuthenticated: boolean
  isLoading: boolean
  user: FocusUser | null

  // Entitlements (apps disponibles según permisos del usuario)
  availableApps: AppMetadata[]
  enableUat: boolean
  orgParams: ClientOrgParams

  // Tab management
  openTabs: AppTab[]
  activeTabId: string | null

  // Session tracking
  sessionId: string        // UUID generado al iniciar sesión
  sessionStartedAt: Date | null

  // UI state
  isSidebarCollapsed: boolean
  commandPaletteOpen: boolean

  // Pop-out windows
  popouts: Map<string, Window>

  // Pop-out tabs (guardados cuando se hace pop-out para poder restaurarlos)
  poppedOutTabs: Map<string, AppTab>

  // Connection status
  connectionStatus: 'connected' | 'degraded' | 'disconnected'
  connectionLatency: number | null
  shellError: string | null
}

/**
 * Actions del Focus Shell store
 * Definidas por separado para mejor type safety
 */
export interface FocusShellActions {
  // Auth actions
  setUser: (user: FocusUser | null) => void
  setAuthenticated: (isAuthenticated: boolean) => void
  setLoading: (isLoading: boolean) => void
  logout: () => void

  // App/Entitlements actions
  setAvailableApps: (apps: AppMetadata[]) => void
  setEnableUat: (enabled: boolean) => void
  setOrgParams: (params: ClientOrgParams) => void

  // Tab actions
  openApp: (appId: string) => void
  closeTab: (tabId: string) => void
  setActiveTab: (tabId: string) => void
  pinTab: (tabId: string) => void
  unpinTab: (tabId: string) => void
  updateTabTitle: (tabId: string, title: string) => void

  // UI actions
  toggleSidebar: () => void
  toggleCommandPalette: () => void

  // Session actions
  initSession: () => void
  loadSession: (userId: string) => void

  // Pop-out actions
  openPopout: (tabId: string) => void
  restoreFromPopout: (instanceId: string) => void

  // Connection actions
  setConnectionStatus: (status: 'connected' | 'degraded' | 'disconnected') => void
  setConnectionLatency: (latency: number | null) => void
  setShellError: (error: string | null) => void
}

/**
 * Combined store type
 */
export type FocusShellStore = FocusShellState & FocusShellActions

/**
 * Apps disponibles en Focus Shell
 *
 * Cada app aquí definida puede ser habilitada por licencia a nivel usuario.
 * El API de entitlements filtra según las licencias activas del usuario.
 */
export const DEFAULT_APPS: AppMetadata[] = [
  {
    appId: 'monitor-contribuciones',
    code: 'MON',
    name: 'Monitor de Contribuciones',
    description: 'Vista en tiempo real de contribuciones FX desde múltiples fuentes',
    icon: '/icons/icon-monitor-contribuciones.svg',
    version: '1.0.0',
    category: 'pricing',
    config: {
      refreshRate: 100,
      defaultSymbols: ['USDMXN', 'EURMXN', 'GBPMXN']
    }
  },
  {
    appId: 'btrader',
    code: 'BTR',
    name: 'BTrader',
    description: 'Trading USD/MXN via Bitso RFQ',
    icon: '/icons/icon-btrader.svg',
    version: '0.1.0',
    category: 'trading',
    config: {
      defaultPair: 'usd_mxn',
      refreshRate: 3000,
    }
  },
  {
    appId: 'cnf',
    code: 'CNF',
    name: 'CNF',
    description: 'Monitor de precios USD/COP en tiempo real',
    icon: '/icons/icon-cnf.svg',
    version: '1.0.0',
    category: 'pricing',
    config: {
      refreshRate: 100,
      defaultSymbols: ['USDCOP'],
    }
  }
]
